import { useEffect, useRef, useState } from 'react';
import Calendar from '../Calendar/Calendar';
import Input from '../Form/Input/Input';

import {
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate,
    useDismiss,
    useInteractions,
} from '@floating-ui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { formattedDate } from '../../utils/calendar.utils';
import { AcceptsSetFormItemError } from '../Form/FormItem/FormItem';

// TODO: Update colors on day button to color tokens
type DatePickerProps = {
    setDate?: Date;
    startDate?: Date | null;
    endDate?: Date | null;
    isRange?: boolean;
    targetId?: string;
    onChange?: (value: string[]) => void;
    children?: React.ReactNode;
    disabled?: boolean;
    testId?: string;
} & AcceptsSetFormItemError;

export default function DatePicker({
    setDate = new Date(),
    startDate,
    endDate,
    isRange = false,
    children,
    onChange,
    disabled = false,
    testId = 'datepicker-input',
}: DatePickerProps) {
    const [inputValue, setInputValue] = useState<string>(
        currentDate(startDate || null, endDate || null, isRange),
    );
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const nextMonth = new Date(startDate ? startDate : setDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const [useStartDate, setUseStartDate] = useState<Date | null>(
        startDate ? startDate : null,
    );
    const [useEndDate, setUseEndDate] = useState<Date | null>(
        endDate ? endDate : null,
    );

    const [isRangeSetDate, _] = useState<Date>(nextMonth);
    const wrapperStyles = isRange
        ? 'alli-w-[528px] alli-max-w-[528px] alli-grid alli-grid-cols-2 alli-gap-4'
        : 'alli-w-64 alli-max-w-64';

    const placeholder = isRange ? 'Select Dates' : 'Select Date';

    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        middleware: [offset(8), flip(), shift()],
        whileElementsMounted: autoUpdate,
        open: isOpen,
        onOpenChange: setIsOpen,
    });

    const dismiss = useDismiss(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

    const handleDateSelection = (date: Date) => {
        const beforeStartDate =
            useStartDate && date.getTime() < useStartDate?.getTime();
        if (isRange) {
            if (
                !useStartDate ||
                (useStartDate && useEndDate) ||
                beforeStartDate
            ) {
                setUseStartDate(date);
                setUseEndDate(null);
            } else {
                setUseEndDate(date);
            }
        } else {
            setUseStartDate(date);
        }
    };

    useEffect(() => {
        if (isRange) {
            if (useStartDate && useEndDate) {
                setInputValue(
                    formatInputDate(isRange, useStartDate, useEndDate),
                );
            } else if (useStartDate && !useEndDate) {
                setInputValue(formatInputDate(isRange, useStartDate, null));
            } else {
                setInputValue('');
            }
        } else {
            if (useStartDate) {
                setInputValue(formatInputDate(isRange, useStartDate, null));
            }
        }
    }, [useStartDate, useEndDate]);

    useEffect(() => {
        const isValid = isValidInputDate(inputValue, isRange);

        if (isValid) {
            setErrorMessage('');
        }

        if (isValid && onChange) {
            if (isRange) {
                if (useStartDate && useEndDate) {
                    onChange([
                        formattedDate(useStartDate),
                        formattedDate(useEndDate),
                    ]);
                    setIsOpen(false);
                } else if (useStartDate) {
                    onChange([formattedDate(useStartDate)]);
                    setIsOpen(false);
                }
            } else if (useStartDate) {
                onChange([formattedDate(useStartDate)]);
                setIsOpen(false);
            }
        }
    }, [inputValue]);
    const handleInputValueChange = (
        value: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const date = value.target.value;
        setInputValue(date);
        const isValid = isValidInputDate(date, isRange);
        if (isValid) {
            const dateArray = date.replaceAll(' ', '').split('-');
            if (isRange) {
                setUseStartDate(new Date(dateArray[0]));
                setUseEndDate(new Date(dateArray[1]));
            } else {
                setUseStartDate(new Date(date));
            }
        }
    };

    const handleOnBlur = () => {
        const isValid = isValidInputDate(inputValue, isRange);

        if (!isValid && inputValue !== '') {
            setErrorMessage(
                'Invalid date format. Please use MM/DD/YYYY or MM/DD/YYYY - MM/DD/YYYY',
            );
        }
    };

    const handleOnFocus = () => {
        setErrorMessage('');
        setIsOpen(true);
    };

    return (
        <>
            <Input
                data-testid={testId}
                disabled={disabled}
                ref={node => {
                    inputRef.current = node;
                    refs.setReference(node);
                }}
                {...getReferenceProps()}
                name="date-picker"
                placeholder={placeholder}
                onChange={handleInputValueChange}
                onBlur={handleOnBlur}
                onFocus={handleOnFocus}
                error={errorMessage}
                value={inputValue}
                trailingIcon={
                    <CalendarIcon
                        className="alli-h-5 alli-w-5"
                        aria-hidden="true"
                    />
                }
            />
            {isOpen && (
                <div
                    ref={refs.setFloating}
                    {...getFloatingProps()}
                    style={{
                        ...floatingStyles,
                        backgroundColor: 'white',
                        zIndex: 9999,
                    }}
                    className={`${wrapperStyles} alli-rounded-xl alli-border alli-border-gray-400 alli-shadow-lg`}>
                    <Calendar
                        onDateChange={handleDateSelection}
                        setupDate={useStartDate ? useStartDate : setDate}
                        isRange={isRange}
                        startDate={useStartDate}
                        endDate={useEndDate}
                    />
                    {isRange && (
                        <Calendar
                            onDateChange={handleDateSelection}
                            setupDate={isRangeSetDate}
                            isRange={isRange}
                            startDate={useStartDate}
                            endDate={useEndDate}
                        />
                    )}
                    {children && (
                        <div className="alli-space alli-col-span-2 alli-mt-2 alli-flex alli-flex-wrap alli-gap-2 alli-gap-y-2 alli-space-x-1">
                            {children}
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

function isValidInputDate(input: string, range: boolean): boolean {
    const singleDatePattern = /^\d{2}\/\d{2}\/\d{4}$/; // MM/DD/YYYY
    const dateRangePattern = /^\d{2}\/\d{2}\/\d{4}\s*-\s*\d{2}\/\d{2}\/\d{4}$/; // MM/DD/YYYY - MM/DD/YYYY

    return range ? dateRangePattern.test(input) : singleDatePattern.test(input);
}

function currentDate(
    startDate: Date | null,
    endDate: Date | null,
    isRange: boolean,
) {
    if (isRange && startDate && endDate)
        return formattedDate(startDate) + ' - ' + formattedDate(endDate);

    if (startDate) return formattedDate(startDate);

    return '';
}

function formatInputDate(
    isRange: boolean,
    startDate: Date,
    endDate: Date | null,
): string {
    if (isRange) {
        const e = endDate ? formattedDate(endDate as Date) : '';
        return formattedDate(startDate) + ' - ' + e;
    }

    return formattedDate(startDate);
}
